import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent,
  CircularProgress, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BadgeIcon from '@mui/icons-material/Badge';
import { login, supervisorLogin } from '../api/client';

export default function LoginPage() {
  const navigate = useNavigate();
  const [tab,      setTab]      = useState(0);   // 0 = admin, 1 = supervisor
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  // Admin form
  const [adminUser, setAdminUser] = useState('');
  const [adminPass, setAdminPass] = useState('');

  // Supervisor form
  const [supEmpId, setSupEmpId] = useState('');
  const [supPass,  setSupPass]  = useState('');

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(adminUser, adminPass);
      localStorage.setItem('access_token', data.access_token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSupervisorSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await supervisorLogin(supEmpId, supPass);
      localStorage.setItem('access_token', data.access_token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f2f5',
    }}>
      <Card sx={{ minWidth: 400, maxWidth: 440, width: '100%', p: 2 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={0.5}>
            Drowsiness Detection
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" mb={2}>
            Fleet Management Dashboard
          </Typography>

          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setError(null); }}
            variant="fullWidth"
            sx={{ mb: 2 }}
          >
            <Tab icon={<AdminPanelSettingsIcon />} iconPosition="start" label="Admin" />
            <Tab icon={<BadgeIcon />}              iconPosition="start" label="Supervisor" />
          </Tabs>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* ── Admin login ── */}
          {tab === 0 && (
            <Box component="form" onSubmit={handleAdminSubmit}>
              <TextField
                label="Username" fullWidth margin="normal"
                value={adminUser} onChange={e => setAdminUser(e.target.value)}
                autoFocus autoComplete="username"
              />
              <TextField
                label="Password" type="password" fullWidth margin="normal"
                value={adminPass} onChange={e => setAdminPass(e.target.value)}
                autoComplete="current-password"
              />
              <Button
                type="submit" variant="contained" fullWidth size="large" sx={{ mt: 2 }}
                disabled={loading || !adminUser || !adminPass}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In as Admin'}
              </Button>
            </Box>
          )}

          {/* ── Supervisor login ── */}
          {tab === 1 && (
            <Box component="form" onSubmit={handleSupervisorSubmit}>
              <TextField
                label="Employee ID (4 digits)" fullWidth margin="normal"
                value={supEmpId}
                onChange={e => setSupEmpId(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus autoComplete="username"
                inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                helperText="Your 4-digit employee ID"
              />
              <TextField
                label="Dashboard Password" type="password" fullWidth margin="normal"
                value={supPass} onChange={e => setSupPass(e.target.value)}
                autoComplete="current-password"
                helperText="Set by your admin"
              />
              <Button
                type="submit" variant="contained" fullWidth size="large" sx={{ mt: 2 }}
                disabled={loading || supEmpId.length !== 4 || !supPass}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In as Supervisor'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}